"use client";

import { useState, useEffect } from "react";

export interface ReducedMotionResult {
  prefersReducedMotion: boolean; // OS preference
  shouldAnimate: boolean; // Final decision (considers user override)
}

export function useReducedMotion(
  userOverride?: boolean
): ReducedMotionResult {
  // Initialize state with the actual value to avoid re-render during mount
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    // SSR safety: check if window is available
    if (typeof window === "undefined") {
      return;
    }

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");

    // No need to set initial value - already set in useState initializer

    // Listen for changes
    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersReducedMotion(event.matches);
    };

    mediaQuery.addEventListener("change", handleChange);

    // Cleanup listener on unmount
    return () => {
      mediaQuery.removeEventListener("change", handleChange);
    };
  }, []);

  // User override logic: if userOverride is explicitly true, animate regardless of OS
  const shouldAnimate =
    userOverride === true ? true : !prefersReducedMotion;

  return {
    prefersReducedMotion,
    shouldAnimate,
  };
}
